import React from 'react';
import { configureFeedback } from './shared/feedback/configure-feedback';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initializeTheme, ThemeProvider } from './styles/theme/ThemeProvider';
import { BrowserRouter } from 'react-router-dom';
import { appPath, routerBasename } from './config/app-path';
import { applyUiLanguage } from './shared/i18n/ui-language-bootstrap';
import { isolateSmartWorkerLaunchSession } from './features/auth/smartworker-session';
import { applyBusinessContextSwitch, automaticReloadAllowed } from './shared/hooks/business-context-switch';
import { shouldRetryReadQuery } from './services/http/query-retry';
import './styles/foundation.css';
import './styles/dashboard-polish.css';
import './styles/shell-regressions.css';
import './styles/flat-buttons.css';
import './styles/page-heading.css';
import './styles/business-workbenches.css';
import './styles/design-system.css';

const App = React.lazy(() => import('./app/App').then((module) => ({ default: module.App })));

// A cancelled navigation must not change the still-mounted page's accounting scope.
// Consume the one-time switch before React mounts or any business query can run.
let contextSwitchFailed = false;
try { applyBusinessContextSwitch({ local: localStorage, pending: sessionStorage, url: window.location.href, now: Date.now(), replaceUrl: (url) => window.history.replaceState(window.history.state, '', url) }); }
catch { contextSwitchFailed = true; }

const buildVersion = import.meta.env.VITE_BUILD_VERSION || 'development';
const uiLanguagePromise = applyUiLanguage();
initializeTheme();

// Establish the SmartWorker callback as a fresh authentication boundary before
// React mounts and before any shell/public-brand query can read an old token.
isolateSmartWorkerLaunchSession(window.location.pathname, localStorage);

async function checkForApplicationUpdate() {
  if (buildVersion === 'development') return;
  try {
    const response = await fetch(`${appPath('/version.json')}?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return;
    const latest = await response.json() as { version?: string };
    if (latest.version && latest.version !== buildVersion && automaticReloadAllowed(window)) window.location.reload();
  } catch {
    // 网络短暂不可用时保留当前页面，下次回到前台或定时检查时再重试。
  }
}

window.addEventListener('focus', () => void checkForApplicationUpdate());
window.setInterval(() => void checkForApplicationUpdate(), 60_000);
void checkForApplicationUpdate();

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false, retry: shouldRetryReadQuery } } });

async function bootstrap() {
  const uiLanguage = await uiLanguagePromise;
  configureFeedback(uiLanguage.antdLocale);
  if (contextSwitchFailed) {
    // Do not mount authenticated queries after a browser-storage commit failure.
    const { localizeUiText } = await import('./shared/i18n/ui-language');
    const root = document.getElementById('root')!;
    const warning = document.createElement('p'); warning.setAttribute('role', 'alert'); warning.textContent = localizeUiText(uiLanguage.code, '切换核算范围失败');
    const retry = document.createElement('button'); retry.textContent = localizeUiText(uiLanguage.code, '重试'); retry.onclick = () => window.location.reload();
    root.replaceChildren(warning, retry); return;
  }
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ThemeProvider locale={uiLanguage.antdLocale}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={routerBasename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <React.Suspense fallback={null}><App /></React.Suspense>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </React.StrictMode>
  );

  // Installs the bundled, offline Simplified→Traditional transformer after React
  // owns the root, so dynamically rendered tables and dialogs are covered too.
  void import('./shared/i18n/ui-language').then(({ installLocalUiTextLanguage }) => installLocalUiTextLanguage(uiLanguage.code));
}

void bootstrap();
